import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Check, ChevronRight, Plus, ShoppingCart } from "lucide-react";
import type { SearchListing } from "../../../api/catalogue";
import { formatPesos } from "../../../lib/money";
import { Button, Card, Icon, Input, Select } from "../../ui";
import { searchPath } from "../SearchBox";

/** What happened to one line when the customer pressed "Add all to basket". */
export type QuickOrderOutcome =
  | { kind: "added"; listing: SearchListing; quantity: number }
  /** A standard, or a number on several parts: the customer picks, nothing is guessed. */
  | { kind: "choose"; standard: boolean; options: SearchListing[] }
  /** An old number: the part that replaced it, offered rather than added. */
  | { kind: "replaced"; replacement: SearchListing }
  | { kind: "out_of_stock"; listing: SearchListing }
  | { kind: "unknown" }
  | { kind: "failed"; message: string };

export interface QuickOrderLine {
  number: string;
  quantity: string;
  outcome: QuickOrderOutcome | null;
}

export const QUICK_ORDER_MAX_LINES = 5;

export interface QuickOrderProps {
  lines: QuickOrderLine[];
  busy: boolean;
  onChange: (index: number, change: Partial<Pick<QuickOrderLine, "number" | "quantity">>) => void;
  onAddLine: () => void;
  onSubmit: () => void;
  /** Adds the part the customer chose for a line: one size of a standard, or a replacement. */
  onPick: (index: number, listing: SearchListing) => void;
}

/** "Know the part number?" (desktop kit Hero): up to five numbers into the basket at once. */
export function QuickOrder({ lines, busy, onChange, onAddLine, onSubmit, onPick }: QuickOrderProps) {
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };
  return (
    <Card padding="var(--space-7)" className="flex flex-col">
      <p className="mb-3 type-eyebrow tracking-[var(--tracking-widest)] text-green-700 uppercase">Quick order</p>
      <h2 className="mb-2 type-h3 [font-size:var(--text-lg)]">Know the part number?</h2>
      <p className="mb-5 type-small text-text-muted">
        Type up to five numbers and we'll add them to your basket. A number shared by several sizes asks which one you need.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-3">
        {lines.map((line, index) => (
          <div key={index} className="flex flex-col gap-2">
            <div className="flex gap-3">
              <Input
                aria-label={`Part number, line ${index + 1}`}
                mono
                size="sm"
                value={line.number}
                onChange={(event) => onChange(index, { number: event.target.value })}
                placeholder="Part number"
                autoComplete="off"
                className="min-w-0 flex-1"
              />
              <Input
                aria-label={`Quantity, line ${index + 1}`}
                mono
                size="sm"
                inputMode="numeric"
                value={line.quantity}
                onChange={(event) => onChange(index, { quantity: event.target.value.replace(/\D/g, "").slice(0, 3) })}
                placeholder="Qty"
                className="w-[74px] flex-none"
              />
            </div>
            {line.outcome && <Outcome number={line.number} outcome={line.outcome} busy={busy} onPick={(listing) => onPick(index, listing)} />}
          </div>
        ))}
        {lines.length < QUICK_ORDER_MAX_LINES && (
          <Button type="button" variant="quiet" size="sm" icon={Plus} onClick={onAddLine} className="self-start">
            Add a line
          </Button>
        )}
        <Button type="submit" icon={ShoppingCart} fullWidth disabled={busy || lines.every((line) => !line.number.trim())} className="mt-2">
          {busy ? "Adding…" : "Add all to basket"}
        </Button>
      </form>
      {/* Pins "View basket" to the card's foot when the banner beside it is taller. */}
      <div className="min-h-6 flex-1" />
      <div className="border-t border-border-subtle pt-5">
        <Link to="/cart" className="flex h-(--control-sm) items-center justify-center gap-1 rounded-sm border border-green-700 type-label [font-size:var(--text-sm)] text-green-800 hover:bg-green-50">
          View basket <Icon icon={ChevronRight} size={16} />
        </Link>
      </div>
    </Card>
  );
}

function Outcome({ number, outcome, busy, onPick }: { number: string; outcome: QuickOrderOutcome; busy: boolean; onPick: (listing: SearchListing) => void }) {
  switch (outcome.kind) {
    case "added":
      return (
        <p role="status" className="m-0 flex items-start gap-1 type-small text-success-700">
          <Icon icon={Check} size={16} className="mt-0.5 flex-none" />
          <span>
            Added {outcome.quantity} × {outcome.listing.name}
          </span>
        </p>
      );
    case "choose":
      return <ChooseOne number={number} outcome={outcome} busy={busy} onPick={onPick} />;
    case "replaced":
      return (
        <div className="flex flex-wrap items-center gap-2 type-small text-ink-700">
          <span>That number has been replaced by {outcome.replacement.name}.</span>
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => onPick(outcome.replacement)}>
            Add {outcome.replacement.reference_number ?? outcome.replacement.sku} instead
          </Button>
        </div>
      );
    case "out_of_stock":
      return <p className="m-0 type-small text-danger-700">{outcome.listing.name} is out of stock, so it wasn't added.</p>;
    case "unknown":
      return (
        <p className="m-0 type-small text-danger-700">
          We don't have a part with this number. Check it, or <Link to={searchPath(number)}>search for it</Link>.
        </p>
      );
    case "failed":
      return (
        <p role="alert" className="m-0 type-small text-danger-700">
          {outcome.message}
        </p>
      );
  }
}

/** Several parts share the number: the customer says which. Out-of-stock ones show but can't be chosen. */
function ChooseOne({ number, outcome, busy, onPick }: { number: string; outcome: Extract<QuickOrderOutcome, { kind: "choose" }>; busy: boolean; onPick: (listing: SearchListing) => void }) {
  const [pickedId, setPickedId] = useState("");
  const picked = outcome.options.find((option) => String(option.id) === pickedId);
  return (
    <div className="flex flex-col gap-2 rounded-md border border-gold-300 bg-surface-tint-gold p-3">
      <p className="m-0 type-small text-ink-700">
        {outcome.standard ? (
          <>
            <span className="type-sku">{number}</span> is a standard: {outcome.options.length} parts are made to it. Which size?
          </>
        ) : (
          <>
            <span className="type-sku">{number}</span> is on {outcome.options.length} parts. Which one?
          </>
        )}
      </p>
      <div className="flex gap-2">
        <Select
          aria-label={`Which part for ${number}`}
          size="sm"
          value={pickedId}
          onChange={(event) => setPickedId(event.target.value)}
          options={[
            { value: "", label: "Choose…" },
            ...outcome.options.map((option) => ({
              value: String(option.id),
              // The book's names often carry the size already ("Bolt M8×12").
              label: [option.name, option.dimension && !option.name.includes(option.dimension) ? option.dimension : null, formatPesos(option.price_cents), option.in_stock ? null : "out of stock"]
                .filter(Boolean)
                .join(" · "),
            })),
          ]}
          className="min-w-0 flex-1"
        />
        <Button type="button" size="sm" disabled={busy || !picked || !picked.in_stock} onClick={() => picked && onPick(picked)}>
          Add
        </Button>
      </div>
      {picked && !picked.in_stock && <p className="m-0 type-small text-danger-700">That one is out of stock.</p>}
    </div>
  );
}
